import { isOffsetDateTime, isUuid } from '@/lib/validation';
import type { ServiceClient } from '../http/client';
import { ApiError } from '../http/errors';
import { ownershipStates } from './inventory';
import type { PricingFieldSource, PricingValues } from './native-pricing-contract';
import {
  decodePricingCommand,
  decodePricingEdit,
  decodeRoomPricingRead,
  decodeTypePricingEditRead,
  validatePricingInput,
} from './native-pricing-contract';
import type { NativeCatalogRoom } from './native-property-read';
import { readRoomSale } from './room-sales';

export interface NativeRoomOwnerships {
  property_id: string;
  room_id: string;
  topology_revision: number;
  selection_status: 'SELECTED' | 'NOT_SELECTED' | 'UNKNOWN';
  selection_version: number | null;
  observed_at: string;
  observation_boundary: 'STORED_ONLY';
  units: {
    unit_id: string;
    ownership: {
      ownership_id: string;
      epoch: number;
      status: (typeof ownershipStates)[number];
      effective_from: string;
      effective_until: string | null;
      mapping_revision: number | null;
    } | null;
  }[];
}

const invalid = () =>
  new ApiError({
    kind: 'invalid-response',
    service: 'core',
    message: '현재 판매 준비 결과를 확인하지 못했습니다.',
  });
const record = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);
const revision = (value: unknown): value is number =>
  Number.isSafeInteger(value) && Number(value) >= 0;
const objectResult = (value: unknown): Record<string, unknown> => {
  if (!record(value)) throw invalid();
  return value;
};

export async function readNativeRoomOwnerships(
  client: ServiceClient,
  room: Pick<
    NativeCatalogRoom,
    'id' | 'property_id' | 'topology_revision' | 'selected' | 'selection_version'
  > & { units: { id: string }[] },
  signal?: AbortSignal,
): Promise<NativeRoomOwnerships> {
  if (!isUuid(room.id)) throw invalid();
  const data = objectResult(
    await client.get<unknown>(`admin/rooms/${room.id}/ownerships`, { signal }),
  );
  const unitIds = new Set(room.units.map((unit) => unit.id));
  const selection =
    room.selected === null ? 'UNKNOWN' : room.selected ? 'SELECTED' : 'NOT_SELECTED';
  if (
    data.room_id !== room.id ||
    data.property_id !== room.property_id ||
    data.topology_revision !== room.topology_revision ||
    data.selection_status !== selection ||
    data.selection_version !== room.selection_version ||
    !isOffsetDateTime(data.observed_at) ||
    data.observation_boundary !== 'STORED_ONLY' ||
    !Array.isArray(data.units) ||
    data.units.length !== unitIds.size
  )
    throw invalid();
  const seen = new Set<string>();
  for (const unit of data.units) {
    if (
      !record(unit) ||
      !isUuid(unit.unit_id) ||
      !unitIds.has(unit.unit_id) ||
      seen.has(unit.unit_id)
    )
      throw invalid();
    seen.add(unit.unit_id);
    const owner = unit.ownership;
    if (owner === null) continue;
    if (
      !record(owner) ||
      !isUuid(owner.ownership_id) ||
      !revision(owner.epoch) ||
      !ownershipStates.includes(owner.status as (typeof ownershipStates)[number]) ||
      !isOffsetDateTime(owner.effective_from) ||
      !(owner.effective_until === null || isOffsetDateTime(owner.effective_until)) ||
      !(owner.mapping_revision === null || revision(owner.mapping_revision))
    )
      throw invalid();
  }
  return data as unknown as NativeRoomOwnerships;
}

export interface TypePricingEdit {
  /** 읽기 전용 하위 가격 영향 개수(관측). 요청·CAS에 쓰지 않는다. */
  descendant_price_impact?: unknown;
  target: { kind: 'TYPE'; type_id: string; property_id: string };
  type_policy: {
    id: string;
    revision: number;
    pricing_policy_code: string;
    currency: string;
    price_basis: string;
    regular_amount_minor: number | null;
    deposit_amount: number | null;
    cleaning_fee: number | null;
    deleted_at: string | null;
  } | null;
  rules: unknown[];
  tiers: unknown[];
  cas: Record<string, unknown>;
}

export interface RoomPricingEdit {
  target: { kind: 'ROOM'; room_id: string; property_id: string };
  type_policy: TypePricingEdit['type_policy'];
  room_policy: TypePricingEdit['type_policy'];
  rules: { type: unknown[]; room: unknown[] };
  tiers: unknown[];
  cas: Record<string, unknown>;
}
export interface RoomPricingRead extends RoomPricingEdit {
  effective_fields: Record<keyof PricingValues, PricingFieldSource>;
}

export async function readRoomPricing(
  client: ServiceClient,
  roomId: string,
  signal?: AbortSignal,
): Promise<RoomPricingRead> {
  if (!isUuid(roomId)) throw invalid();
  const data = await client.get<unknown>(`admin/rooms/${roomId}/pricing`, { signal });
  decodeRoomPricingRead(data, roomId);
  return data as RoomPricingRead;
}

export async function readRoomPricingEdit(
  client: ServiceClient,
  roomId: string,
  signal?: AbortSignal,
): Promise<RoomPricingEdit> {
  if (!isUuid(roomId)) throw invalid();
  const data = objectResult(
    await client.get<unknown>(`admin/rooms/${roomId}/pricing`, {
      params: { view: 'edit' },
      signal,
    }),
  );
  decodePricingEdit(data, 'ROOM', roomId);
  return data as unknown as RoomPricingEdit;
}

export async function setRoomPricing(
  client: ServiceClient,
  roomId: string,
  input: {
    command_id: string;
    expected: Record<string, unknown>;
    values: PricingValues;
    reason: string;
  },
) {
  validatePricingInput(input, 'ROOM', roomId);
  return decodePricingCommand(
    await client.put<unknown>(`admin/rooms/${roomId}/pricing`, {
      json: input,
    }),
    'ROOM',
    roomId,
    input.command_id,
  );
}

export async function readTypePricingEdit(
  client: ServiceClient,
  typeId: string,
  signal?: AbortSignal,
): Promise<TypePricingEdit> {
  if (!isUuid(typeId)) throw invalid();
  const data = objectResult(
    await client.get<unknown>(`admin/room-types/${typeId}/pricing`, {
      params: { view: 'edit' },
      signal,
    }),
  );
  decodeTypePricingEditRead(data, typeId);
  return data as unknown as TypePricingEdit;
}

export async function setTypePricing(
  client: ServiceClient,
  typeId: string,
  input: {
    command_id: string;
    expected: Record<string, unknown>;
    values: PricingValues;
    reason: string;
  },
) {
  validatePricingInput(input, 'TYPE', typeId);
  return decodePricingCommand(
    await client.put<unknown>(`admin/room-types/${typeId}/pricing`, {
      json: input,
    }),
    'TYPE',
    typeId,
    input.command_id,
  );
}

export async function setNativeMidtermSale(
  client: ServiceClient,
  roomId: string,
  input: {
    command_id: string;
    expected_sale_revision: number;
    first_move_in_date: string;
    daily_price_minor?: number;
    reason: string;
  },
) {
  if (
    !isUuid(roomId) ||
    !isUuid(input.command_id) ||
    !revision(input.expected_sale_revision) ||
    (Object.hasOwn(input, 'daily_price_minor') &&
      (!Number.isSafeInteger(input.daily_price_minor) || Number(input.daily_price_minor) <= 0)) ||
    !/^\d{4}-\d{2}-\d{2}$/.test(input.first_move_in_date) ||
    !input.reason.trim() ||
    input.reason.length > 500
  )
    throw invalid();
  const data = objectResult(
    await client.put<unknown>(`admin/rooms/${roomId}/native-midterm-sale`, { json: input }),
  );
  if (
    data.command_id !== input.command_id ||
    data.room_id !== roomId ||
    data.product !== 'midterm' ||
    !revision(data.revision)
  )
    throw invalid();
  return data;
}

// Compare pricing state only: sale/authority CAS may change during a sale-only transition.
// Rules and tiers are sets here; object key order and list order do not change their meaning.
export function sameRoomPricingState(before: RoomPricingRead, after: RoomPricingRead) {
  const canonical = (value: unknown): string => {
    if (Array.isArray(value)) return `[${value.map(canonical).sort().join(',')}]`;
    if (value !== null && typeof value === 'object')
      return `{${Object.entries(value)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([key, item]) => `${JSON.stringify(key)}:${canonical(item)}`)
        .join(',')}}`;
    if (value === undefined) return 'undefined';
    return JSON.stringify(value);
  };
  const state = (read: RoomPricingRead) => ({
    target: {
      kind: read.target.kind,
      property_id: read.target.property_id,
      room_id: read.target.room_id,
    },
    type_policy: read.type_policy,
    room_policy: read.room_policy,
    rules: read.rules,
    tiers: read.tiers,
    effective_fields: read.effective_fields,
  });
  return canonical(state(before)) === canonical(state(after));
}

// Both transition paths must observe the selected Room's committed price and sale before completion.
export async function readAppliedMidtermSale(
  client: ServiceClient,
  roomId: string,
  expected: {
    daily_price_minor?: number;
    first_move_in_date: string;
    revision: number;
    preservedPricing?: RoomPricingRead;
  },
) {
  if (expected.daily_price_minor === undefined) {
    const [sale, effective] = await Promise.all([
      readRoomSale(client, roomId),
      readRoomPricing(client, roomId),
    ]);
    if (
      !expected.preservedPricing ||
      !sameRoomPricingState(expected.preservedPricing, effective) ||
      sale.product !== 'midterm' ||
      sale.channel !== 'own_website' ||
      sale.first_move_in_date !== expected.first_move_in_date ||
      sale.revision !== expected.revision ||
      sale.pending_outcome !== null
    )
      throw invalid();
    return { sale, effective };
  }
  const [pricing, sale, effective] = await Promise.all([
    readRoomPricingEdit(client, roomId),
    readRoomSale(client, roomId),
    readRoomPricing(client, roomId),
  ]);
  const policy = pricing.room_policy;
  if (
    !policy ||
    policy.deleted_at ||
    policy.pricing_policy_code !== 'MIDTERM' ||
    policy.currency !== 'KRW' ||
    policy.price_basis !== 'DAY' ||
    policy.regular_amount_minor !== expected.daily_price_minor ||
    policy.deposit_amount !== 0 ||
    policy.cleaning_fee !== 0 ||
    effective.effective_fields.regular_amount_minor.value !== expected.daily_price_minor ||
    effective.effective_fields.deposit_amount.value !== 0 ||
    effective.effective_fields.cleaning_fee.value !== 0 ||
    Object.values(effective.effective_fields).some(
      (field) =>
        field.reason !== null ||
        field.source_policy_id !== policy.id ||
        field.revision !== policy.revision,
    ) ||
    sale.daily_price_minor !== expected.daily_price_minor ||
    sale.price_currency !== 'KRW' ||
    sale.product !== 'midterm' ||
    sale.channel !== 'own_website' ||
    sale.first_move_in_date !== expected.first_move_in_date ||
    sale.revision !== expected.revision ||
    sale.pending_outcome !== null
  )
    throw invalid();
  return { pricing, sale, effective };
}

export async function reverseNativeMidtermSale(
  client: ServiceClient,
  roomId: string,
  input: { command_id: string; expected_sale_revision: number; reason: string },
) {
  if (
    !isUuid(roomId) ||
    !isUuid(input.command_id) ||
    !revision(input.expected_sale_revision) ||
    input.expected_sale_revision === 0 ||
    !input.reason.trim() ||
    input.reason.length > 500
  )
    throw invalid();
  const data = objectResult(
    await client.post<unknown>(`admin/rooms/${roomId}/native-sales-transition`, {
      json: {
        ...input,
        target_product: 'long_term',
        channel: 'own_website',
        first_move_in_date: null,
        recontract_confirmed: false,
        reverse_confirmed: true,
      },
    }),
  );
  if (
    data.command_id !== input.command_id ||
    data.room_id !== roomId ||
    data.product !== 'long_term' ||
    data.channel !== 'own_website' ||
    data.first_move_in_date !== null ||
    !revision(data.revision) ||
    data.revision <= input.expected_sale_revision ||
    data.provider_outcome !== null
  )
    throw invalid();
  return data;
}

export async function activateNativeRoom(
  client: ServiceClient,
  roomId: string,
  input: {
    command_id: string;
    expected_topology_revision: number;
    expected_selection_revision: number;
    unit_ids: string[];
    effective_from: string;
    effective_until: string | null;
  },
) {
  if (
    !isUuid(roomId) ||
    !isUuid(input.command_id) ||
    !revision(input.expected_topology_revision) ||
    input.expected_topology_revision === 0 ||
    !revision(input.expected_selection_revision) ||
    input.expected_selection_revision === 0 ||
    input.unit_ids.length === 0 ||
    input.unit_ids.some((id) => !isUuid(id)) ||
    new Set(input.unit_ids).size !== input.unit_ids.length ||
    !isOffsetDateTime(input.effective_from) ||
    !isOffsetDateTime(input.effective_until) ||
    Date.parse(input.effective_until) <= Date.parse(input.effective_from)
  )
    throw invalid();
  const data = objectResult(
    await client.post<unknown>(`admin/rooms/${roomId}/activation`, { json: input }),
  );
  if (
    data.command_id !== input.command_id ||
    data.room_id !== roomId ||
    data.authority !== 'CORE_OWNED' ||
    data.status !== 'ACTIVE' ||
    data.topology_revision !== input.expected_topology_revision ||
    data.selection_revision !== input.expected_selection_revision ||
    !Array.isArray(data.ownerships) ||
    data.ownerships.length !== input.unit_ids.length
  )
    throw invalid();
  return data;
}
